import { Link, useLocation } from 'react-router-dom';
import {
  LayoutGrid,
  Target,
  PieChart,
  Users,
  Banknote,
  BookOpen,
  MapPin,
  X,
} from 'lucide-react';
import { cn } from '../../lib/utils';

interface NavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
  badge?: number;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const navigation: NavSection[] = [
  {
    title: 'Main',
    items: [
      { label: 'Overview', href: '/wireframes', icon: <LayoutGrid className="w-[18px] h-[18px]" /> },
      { label: 'Detection', href: '/wireframes/detection', icon: <Target className="w-[18px] h-[18px]" />, badge: 3 },
      { label: 'Analysis', href: '/wireframes/analysis', icon: <PieChart className="w-[18px] h-[18px]" /> },
      { label: 'Operations', href: '/wireframes/operations', icon: <Users className="w-[18px] h-[18px]" />, badge: 2 },
      { label: 'Cash Assistance', href: '/wireframes/cash', icon: <Banknote className="w-[18px] h-[18px]" /> },
    ],
  },
  {
    title: 'Resources',
    items: [
      { label: 'Knowledge Hub', href: '/wireframes/knowledge', icon: <BookOpen className="w-[18px] h-[18px]" /> },
      { label: 'Crisis Map', href: '/wireframes/map', icon: <MapPin className="w-[18px] h-[18px]" /> },
    ],
  },
];

interface WireframeSidebarProps {
  className?: string;
  isOpen?: boolean;
  onClose?: () => void;
  embedded?: boolean;
}

export function WireframeSidebar({ className, isOpen, onClose, embedded }: WireframeSidebarProps) {
  const location = useLocation();

  // In embedded mode, render a compact always-visible sidebar
  if (embedded) {
    return (
      <aside
        className={cn(
          'w-48 bg-white border-r border-gray-200 flex flex-col flex-shrink-0',
          className
        )}
      >
        {/* Compact Header */}
        <div className="px-3 py-3 border-b border-gray-200">
          <Link to="/wireframes?embedded=true" className="block">
            <div className="text-lg font-bold text-accent-500 tracking-tight">CLEAR</div>
            <div className="text-[9px] text-gray-500">Response Dashboard</div>
          </Link>
        </div>

        {/* Compact Navigation */}
        <nav className="flex-1 px-2 py-2 overflow-y-auto">
          {navigation.map((section) => (
            <div key={section.title} className="mb-2">
              <div className="px-2 py-1 text-[9px] font-semibold text-gray-500 uppercase tracking-wide">
                {section.title}
              </div>
              {section.items.map((item) => {
                const isActive =
                  location.pathname === item.href ||
                  (item.href === '/wireframes' && location.pathname === '/wireframes') ||
                  (item.href !== '/wireframes' && location.pathname.startsWith(item.href));

                // Preserve embedded query param in links
                const embeddedHref = `${item.href}?embedded=true`;

                return (
                  <Link
                    key={item.href}
                    to={embeddedHref}
                    className={cn(
                      'flex items-center gap-1.5 px-2 py-1.5 text-[11px] font-medium transition-all',
                      isActive
                        ? 'bg-accent-50 text-accent-500'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    )}
                  >
                    <span className={cn('opacity-70 [&_svg]:w-[14px] [&_svg]:h-[14px]', isActive && 'opacity-100')}>
                      {item.icon}
                    </span>
                    <span className="truncate">{item.label}</span>
                    {item.badge && (
                      <span className="ml-auto bg-red-600 text-white text-[8px] font-semibold px-1 py-0.5 min-w-[14px] text-center">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Compact Data Layers */}
        {location.pathname === '/wireframes' && (
          <div className="px-2 py-2 border-t border-gray-200 mt-auto">
            <div className="px-2 pb-1 text-[9px] font-semibold text-gray-500 uppercase tracking-wide">
              Data Layers
            </div>
            <div className="space-y-0.5">
              <LayerToggle color="red-600" label="Cholera Cases" defaultChecked compact />
              <LayerToggle color="amber-500" label="Flood Risk" defaultChecked compact />
              <LayerToggle color="amber-600" label="Drought Zones" defaultChecked compact />
              <LayerToggle color="green-600" label="Response Teams" defaultChecked compact />
            </div>
          </div>
        )}
      </aside>
    );
  }

  return (
    <>
      {/* Mobile overlay backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'w-64 bg-white border-r border-gray-200 flex-col flex-shrink-0 z-50',
          // Desktop: always visible, Mobile: hidden by default
          isOpen
            ? 'fixed inset-y-0 left-0 flex lg:relative'
            : 'hidden lg:flex',
          className
        )}
      >
        {/* Header */}
        <div className="px-5 py-5 border-b border-gray-200 flex items-center justify-between">
          <Link to="/wireframes" className="block" onClick={onClose}>
            <div className="text-xl font-bold text-accent-500 tracking-tight">CLEAR</div>
            <div className="text-[11px] text-gray-500 mt-0.5">Response Dashboard</div>
          </Link>
          {/* Mobile close button */}
          <button
            onClick={onClose}
            className="lg:hidden w-8 h-8 flex items-center justify-center hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-3 overflow-y-auto">
          {navigation.map((section) => (
            <div key={section.title} className="mb-4">
              <div className="px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                {section.title}
              </div>
              {section.items.map((item) => {
                const isActive =
                  location.pathname === item.href ||
                  (item.href === '/wireframes' && location.pathname === '/wireframes') ||
                  (item.href !== '/wireframes' && location.pathname.startsWith(item.href));

                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={onClose}
                    className={cn(
                      'flex items-center gap-2.5 px-3 py-2.5 text-[13px] font-medium rounded-none transition-all',
                      isActive
                        ? 'bg-accent-50 text-accent-500'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    )}
                  >
                    <span className={cn('opacity-70', isActive && 'opacity-100')}>
                      {item.icon}
                    </span>
                    <span>{item.label}</span>
                    {item.badge && (
                      <span className="ml-auto bg-red-600 text-white text-[10px] font-semibold px-1.5 py-0.5 min-w-[18px] text-center">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Data Layers (shown on Overview page) */}
        {location.pathname === '/wireframes' && (
          <div className="px-3 py-4 border-t border-gray-200 mt-auto">
            <div className="px-3 pb-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
              Data Layers
            </div>
            <div className="space-y-1">
              <LayerToggle color="red-600" label="Cholera Cases" defaultChecked />
              <LayerToggle color="amber-500" label="Flood Risk" defaultChecked />
              <LayerToggle color="amber-600" label="Drought Zones" defaultChecked />
              <LayerToggle color="green-600" label="Response Teams" defaultChecked />
            </div>
          </div>
        )}
      </aside>
    </>
  );
}

interface LayerToggleProps {
  color: string;
  label: string;
  defaultChecked?: boolean;
  compact?: boolean;
}

function LayerToggle({ color, label, defaultChecked, compact }: LayerToggleProps) {
  const colorClass = `bg-${color}`;

  return (
    <label className={cn(
      "flex items-center gap-2 cursor-pointer hover:bg-gray-50",
      compact ? "px-2 py-1 text-[10px]" : "px-3 py-2 text-[12px]"
    )}>
      <input
        type="checkbox"
        defaultChecked={defaultChecked}
        className={cn(
          "rounded-none border-gray-300",
          compact ? "w-3 h-3" : "w-3.5 h-3.5"
        )}
        style={{ accentColor: color.includes('red') ? '#DC2626' : color.includes('amber') ? '#D97706' : '#059669' }}
      />
      <span className={cn(compact ? 'w-1.5 h-1.5' : 'w-2 h-2', colorClass)} style={{
        backgroundColor: color.includes('red') ? '#DC2626' :
                         color.includes('amber-5') ? '#F59E0B' :
                         color.includes('amber-6') ? '#D97706' : '#059669'
      }} />
      <span className="text-gray-700 truncate">{label}</span>
    </label>
  );
}

export default WireframeSidebar;
